import { useRef } from 'react'
import { Autocomplete } from '@mui/material'
import { CaseOrCategory, Grouped, GroupedCaseOrCategory, isCase } from './types'
import Input from './Input'
import Option from './Option'
import { useFocusOnOpen } from '~/shared/hooks/useFocusOnOpen'

type Props = {
  options: Grouped<CaseOrCategory>[]
  onChange: (value: CaseOrCategory) => void
  onBlur?: () => void
}

export default function TriageCaseOrCategorySelect({ onChange, options, onBlur }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  // The picker mounts when the user asks to triage a message, so focus follows that action.
  useFocusOnOpen(inputRef)

  return (
    <Autocomplete<GroupedCaseOrCategory, undefined, undefined, true>
      id="triage-case-category"
      freeSolo
      openOnFocus
      onBlur={onBlur}
      onKeyDown={e => {
        if (onBlur && e.key === 'Escape') {
          onBlur()
          e.stopPropagation()
        }
      }}
      options={options.filter(caseOrCategory => {
        if (!isCase(caseOrCategory))
          return !caseOrCategory.is_system_only && !caseOrCategory.is_deprecated
        else return caseOrCategory
      })}
      groupBy={option => option.group}
      getOptionLabel={option => (typeof option === 'string' ? option : option.title)}
      renderInput={params => <Input {...params} inputRef={inputRef} />}
      renderOption={({ key: _, ...props }, option) => (
        <Option {...props} option={option} key={option.id} group={option.group} />
      )}
      onChange={(e, newValue, reason) => {
        e.stopPropagation()
        if (reason === 'selectOption' && newValue && typeof newValue !== 'string') {
          onChange(newValue)
        }
      }}
      sx={{ '& .MuiAutocomplete-input': { fontSize: '1.4rem' } }}
      slotProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { fontSize: '1.4rem' } } } }}
    />
  )
}
